/**
 * AMEX Estados de Cuenta
 * src/pages/AmexEstados.jsx
 *
 * Views:
 *  'detail'     – current statement with ← → navigation
 *  'tendencias' – multi-month bar charts
 *  'import'     – upload PDF → auto-parse
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { collection, getDocs, setDoc, doc, serverTimestamp } from 'firebase/firestore';
import { CreditCard, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
         Upload, ArrowLeft, TrendingUp } from 'lucide-react';
import { showToast } from '../components/Toast';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

// ─── Categories ──────────────────────────────────────────────────────────────
const CATS = {
  'Salud':       { emoji:'🏥', color:'#ef4444' },
  'Gasolina':    { emoji:'⛽', color:'#f97316' },
  'Transporte':  { emoji:'🚗', color:'#f59e0b' },
  'Comida':      { emoji:'🍔', color:'#fb923c' },
  'Despensa':    { emoji:'🛒', color:'#10b981' },
  'Ropa':        { emoji:'👕', color:'#8b5cf6' },
  'Tecnología':  { emoji:'💻', color:'#3b82f6' },
  'Compras':     { emoji:'📦', color:'#06b6d4' },
  'Viajes':      { emoji:'✈️', color:'#84cc16' },
  'Servicios':   { emoji:'📡', color:'#6366f1' },
  'Personal':    { emoji:'💈', color:'#ec4899' },
  'Restaurante': { emoji:'🍽️', color:'#14b8a6' },
  'Educación':   { emoji:'📚', color:'#a78bfa' },
  'Diferidos':   { emoji:'📅', color:'#64748b' },
};

// ─── Auto-categorise a charge description ────────────────────────────────────
function autoCat(desc) {
  const d = (desc || '').toUpperCase();
  if (/\bUBER TRIP\b/.test(d))                                                          return 'Transporte';
  if (/UBER EATS|UBRPAGOS/.test(d))                                                     return 'Comida';
  if (/\bDIDI\b|DEMERGE|TAXI\b|CABIFY/.test(d))                                         return 'Transporte';
  if (/CITY MARKET|WALMART|SORIANA|CHEDRAUI|COSTCO|SUPERAMA/.test(d))                  return 'Despensa';
  if (/BUPA|MAPFRE|HOSPITAL|HOSP\b|MEDICA|FARMAC|CLINICA|TEPEYAC|FARMARED/.test(d))    return 'Salud';
  if (/GASOL|GASOLINERA|NETPAY\*GAS|PEMEX|SHELL|OXXO GAS|GAS URIBE|HIDROSI/.test(d)) return 'Gasolina';
  if (/APPLE\b|SAMSUNG/.test(d))                                                         return 'Tecnología';
  if (/NETFLIX|SPOTIFY|YOUTUBE|DISNEY|HBO|PRIME|PARAMOUNT/.test(d))                    return 'Servicios';
  if (/STARLINK|LINKEDIN|GOOGLE CLOUD|SQUARESPACE|AT&T|TELMEX|IZZI|TOTALPLAY|MSBILL|PAYU/.test(d)) return 'Servicios';
  if (/AEROMEXICO|AEROVIAS|VOLARIS|HOTEL|AIRBNB|BOOKING|XCARET|PLAN N TRAVEL|TRAVEL SERVICES|EXPEDIA/.test(d)) return 'Viajes';
  if (/RESTAUR|REST\b|CAFE\b|SUSHI|PIZZA|BURGER|TACO|HOOTERS|VIPS|APPLEBEE|CHILI|MORITAS|MANDOLINA|MARISCOCHO|PROSECCO|ANIMAL\b|SIRENA/.test(d)) return 'Restaurante';
  if (/SANBORNS/.test(d))                                                                 return 'Restaurante';
  if (/ZARA|LEVIS|LEVI'S|BOGGI|RAPSODIA|H&M|CAMPER|LIV SANTA/.test(d))               return 'Ropa';
  if (/PALACIO DE HIERRO|EL PALACIO|PALACIO HIERRO|CINEPOLIS|FEVER\*/.test(d))         return 'Compras';
  if (/AMAZON|MERCADO LIBRE/.test(d))                                                    return 'Tecnología';
  if (/MERCADO PAGO|PAYPAL/.test(d))                                                     return 'Compras';
  if (/PELUQ|BARB|SALON|SPA|HOMME/.test(d))                                              return 'Personal';
  return 'Compras';
}

// ─── AMEX PDF text parser ─────────────────────────────────────────────────────
const MES = { enero:1,febrero:2,marzo:3,abril:4,mayo:5,junio:6,julio:7,agosto:8,septiembre:9,octubre:10,noviembre:11,diciembre:12 };
const MES_SHORT = ['','Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
const p2 = n => String(n).padStart(2,'0');
const pf = s => parseFloat((s||'').replace(/,/g,'')) || 0;
const MONTH_ABR_MAP = { ene:1,feb:2,mar:3,abr:4,may:5,jun:6,jul:7,ago:8,sep:9,oct:10,nov:11,dic:12 };

function parseAmexText(raw) {
  // 1 ─ Period: "Del 4 de Febrero al 3 de Marzo de 2026"
  const pm = raw.match(/Del\s+(\d+)\s+de\s+(\w+)\s+al\s+(\d+)\s+de\s+(\w+)\s+de\s+(\d{4})/i);
  let period='', cutDate='', label='', year='2026';
  if (pm) {
    const [,d1,m1,d2,m2,yr] = pm;
    year = yr;
    const mn2 = MES[m2.toLowerCase()];
    period  = `${d1} ${m1.charAt(0).toUpperCase()+m1.slice(1)} – ${d2} ${m2.charAt(0).toUpperCase()+m2.slice(1)} ${yr}`;
    cutDate = `${yr}-${p2(mn2)}-${p2(d2)}`;
    label   = `${MES_SHORT[mn2]} ${yr}`;
  }

  // 2 ─ Due date: "Fecha límite de pago: 17 de Marzo 2026"
  const dm = raw.match(/[Ff]echa\s+l[íi]mite[^:]*:\s*(\d+)\s+de\s+(\w+)(?:\s+(\d{4}))?/);
  let dueDate = '';
  if (dm) {
    const mn = MES[dm[2].toLowerCase()];
    if (mn) dueDate = `${dm[3]||year}-${p2(mn)}-${p2(dm[1])}`;
  }

  // 3 ─ Summary table: "257,383.84 - 262,559.77 + 224,036.98 = 218,861.05"
  const sm = raw.match(/(\d[\d,]*\.\d{2})\s*-\s*(\d[\d,]*\.\d{2})\s*\+\s*(\d[\d,]*\.\d{2})\s*=\s*(\d[\d,]*\.\d{2})/);
  const prevBalance = sm ? pf(sm[1]) : 0;
  const payments    = sm ? pf(sm[2]) : 0;
  const newCharges  = sm ? pf(sm[3]) : 0;
  const balance     = sm ? pf(sm[4]) : 0;

  // 4 ─ Commissions
  const cm  = raw.match(/Total de Comisiones[^:]*:\s*\$?\s*(\d[\d,]*\.\d{2})/i);
  const commissions = cm ? pf(cm[1]) : 0;

  // 5 ─ Charges — split by person section header
  const charges = [];
  const MES_NAMES = Object.keys(MES).join('|');
  const DATE_RE   = new RegExp(`^(\\d{1,2})\\s+de\\s+(${MES_NAMES})\\s+(.+)$`, 'i');
  const sections  = raw.split(/(?=Nuevos cargos y abonos de )/i);

  sections.forEach(section => {
    const hm = section.match(/Nuevos cargos y abonos de ([^\n]+)/i);
    if (!hm) return;
    const person = hm[1].toLowerCase().includes('alberto') ? 'Alberto' : 'Lilia';

    // Stop at totals / plan summaries
    const body = section
      .replace(/Total de (?:las transacciones|Cargos)[\s\S]*/i, '')
      .replace(/Resumen de Planes[\s\S]*/i, '')
      .replace(/Descripción de cargos[\s\S]*/i, '');

    const rawLines = body.split('\n').map(l => l.trim()).filter(Boolean);
    // Merge two-line dates found in 2025 PDFs (narrow date column causes wrap):
    // "3 de DIDI CIUDAD DE MEXIC 47.00"  (day + desc + amount on same Y)
    // "Septiembre RFCDME180122DU4..."     (month wrapped to next Y)
    // → "3 de Septiembre DIDI CIUDAD DE MEXIC 47.00"
    const MES_START_RE2 = new RegExp(`^(${MES_NAMES})\\b`, 'i');
    const SPLIT_DATE_RE = new RegExp(`^(\\d{1,2})\\s+de\\s+(?!(?:${MES_NAMES})\\b)(.+)$`, 'i');
    const lines = [];
    for (let k = 0; k < rawLines.length; k++) {
      const sd = rawLines[k].match(SPLIT_DATE_RE);
      if (sd && k + 1 < rawLines.length) {
        const mo = rawLines[k+1].match(MES_START_RE2);
        if (mo) { lines.push(`${sd[1]} de ${mo[1]} ${sd[2]}`); k++; continue; }
      }
      lines.push(rawLines[k]);
    }

    let i = 0;
    while (i < lines.length) {
      const line = lines[i];

      // Each charge starts with "DD de Month rest..."
      const dm2 = line.match(DATE_RE);
      if (!dm2) { i++; continue; }

      const [, day, monthStr, rest] = dm2;
      const mn = MES[monthStr.toLowerCase()];
      if (!mn) { i++; continue; }
      const date = `${year}-${p2(mn)}-${p2(day)}`;

      let desc = '';
      let amount = 0;
      let isMsi  = false;
      let isCredit = false;
      let j = i + 1;

      // ── Case A: amount at END of same line (PDF table layout) ──────────────
      // e.g. "4 de Enero UBER TRIP HTTPS://HELP.UB 147.91"
      const inlineMt = rest.match(/^(.+?)\s+(\d[\d,]*\.\d{2})$/);
      if (inlineMt) {
        desc   = inlineMt[1].replace(/RFC\w+\S*\s*/g, '').replace(/HTTPS?:\/\/\S+/g, '').trim();
        amount = pf(inlineMt[2]);
        // Scan following lines for MSI tag or credit marker
        while (j < Math.min(i + 6, lines.length)) {
          const nxt = lines[j];
          if (/^CARGO\s+\d+\s+DE\s+\d+$/i.test(nxt))    { isMsi = true; j++; continue; }
          if (/^RFC\w|^\/REF|\d{6,}/i.test(nxt))          { j++; continue; }
          if (nxt === 'CR')                                 { isCredit = true; j++; break; }
          break; // next charge line or unrecognised
        }

      } else {
        // ── Case B: amount on a separate bare line (foreign-currency charges) ─
        desc = rest.replace(/RFC\w+\S*\s*/g, '').replace(/HTTPS?:\/\/\S+/g, '').trim();
        while (j < Math.min(i + 8, lines.length)) {
          const nxt = lines[j];
          if (/^CARGO\s+\d+\s+DE\s+\d+$/i.test(nxt))    { isMsi = true; j++; continue; }
          if (/^RFC\w|^\/REF|\d{6,}|^[A-Z][a-záéíóúü]+\s+U\.?S\.?A|^Euro\b/i.test(nxt)) { j++; continue; }
          const am = nxt.match(/^(\d[\d,]+\.\d{2})$/);
          if (am) {
            amount = pf(am[1]); j++;
            if (j < lines.length && lines[j] === 'CR') { isCredit = true; j++; }
            break;
          }
          if (DATE_RE.test(nxt)) break; // next charge — stop looking
          j++;
        }
      }

      if (amount > 0 && !isCredit && desc.length > 2) {
        const clean = desc.replace(/\s+/g, ' ').trim();
        charges.push({ date, desc: clean, person, cat: autoCat(clean), amount, msi: isMsi });
      }
      i = j;
    }
  });

  // 6 ─ MSI Plans (Meses sin Intereses)
  // Table row (Y-grouped): "DESC DD de Mon ORIGINAL RATE% PENDING N de M MONTHLY"
  const msiPlans = [];
  const MONTH_ABR = 'ene|feb|mar|abr|may|jun|jul|ago|sep|oct|nov|dic';
  const planRe = new RegExp(
    `^(.+?)\\s+(\\d+)\\s+de\\s+(${MONTH_ABR})\\w*\\s+([\\d,]+\\.\\d{2})\\s+[\\d.]+%\\s*([\\d,]+\\.\\d{2})\\s+(\\d+)\\s+de\\s+(\\d+)\\s+([\\d,]+\\.\\d{2})$`,
    'i'
  );
  const msiBlock = raw.match(
    /Resumen de Planes de Pagos Diferidos con Intereses y Meses sin Intereses([\s\S]+?)Totales de Planes de Pagos Diferidos con Intereses/i
  );
  if (msiBlock) {
    msiBlock[1].split('\n').map(l => l.trim()).forEach((line, idx) => {
      const m = line.match(planRe);
      if (m && !/^mensualidad/i.test(line)) {
        const origMon = MONTH_ABR_MAP[m[3].toLowerCase().slice(0,3)] || 1;
        const stmtMon = cutDate ? parseInt(cutDate.slice(5,7)) : 12;
        const origYr  = origMon > stmtMon ? parseInt(year) - 1 : parseInt(year);
        msiPlans.push({
          id:           `msi-${idx}`,
          desc:         m[1].trim(),
          origDate:     `${origYr}-${p2(origMon)}-${p2(parseInt(m[2]))}`,
          origAmt:      pf(m[4]),
          pending:      pf(m[5]),
          currentMonth: parseInt(m[6]),
          totalMonths:  parseInt(m[7]),
          monthly:      pf(m[8]),
          person:       '',
        });
      }
    });
  }

  // 7 ─ Pagos Diferidos (with commission)
  // Table row: "PLAN DE PAGOS DIFERIDOS DD de Mon MONTO RATE% PENDING N de M MONTHLY"
  const pagosDiferidos = [];
  const difRe = new RegExp(
    `^PLAN\\s+DE\\s+PAGOS\\s+DIFERIDOS\\s+(\\d+)\\s+de\\s+(${MONTH_ABR})\\w*\\s+([\\d,]+\\.\\d{2})\\s+[\\d.]+%\\s*([\\d,]+\\.\\d{2})\\s+(\\d+)\\s+de\\s+(\\d+)\\s+([\\d,]+\\.\\d{2})$`,
    'i'
  );
  const difBlock = raw.match(
    /Resumen de Planes de Pagos Diferidos con Comisi[oó]n([\s\S]+?)Totales de Planes de Pagos Diferidos con Comisi[oó]n/i
  );
  if (difBlock) {
    difBlock[1].split('\n').map(l => l.trim()).forEach((line, idx) => {
      const m = line.match(difRe);
      if (m) {
        const origMon = MONTH_ABR_MAP[m[2].toLowerCase().slice(0,3)] || 1;
        const stmtMon = cutDate ? parseInt(cutDate.slice(5,7)) : 12;
        const origYr  = origMon > stmtMon ? parseInt(year) - 1 : parseInt(year);
        pagosDiferidos.push({
          id:           `dif-${idx}`,
          origDate:     `${origYr}-${p2(origMon)}-${p2(parseInt(m[1]))}`,
          montoDiferido: pf(m[3]),
          comision:     3.0,
          pending:      pf(m[4]),
          currentMonth: parseInt(m[5]),
          totalMonths:  parseInt(m[6]),
          monthly:      pf(m[7]),
        });
      }
    });
  }

  const id = cutDate ? cutDate.slice(0,7) : `stmt-${Date.now()}`;
  return { id, label, period, cutDate, dueDate, balance, prevBalance, payments, newCharges, commissions,
           charges, msiPlans, pagosDiferidos, _parsed: true };
}

// ─── Seed statement: March 2026 (Feb 4 – Mar 3) ──────────────────────────────
const SEED = {
  id:'2026-03', label:'Mar 2026', period:'4 Feb – 3 Mar 2026',
  cutDate:'2026-03-03', dueDate:'2026-03-17',
  balance:218861.05, prevBalance:257383.84, payments:262559.77,
  newCharges:224036.98, commissions:16012.97,
  charges:[
    {date:'2026-02-06',desc:'PELUQ HOMME LUXURY BARB',   person:'Alberto',cat:'Personal',  amount:3480.00},
    {date:'2026-02-07',desc:'UBER TRIP',                  person:'Alberto',cat:'Transporte',amount:126.10},
    {date:'2026-02-08',desc:'UBER TRIP',                  person:'Alberto',cat:'Transporte',amount:229.38},
    {date:'2026-02-13',desc:'UBER TRIP',                  person:'Alberto',cat:'Transporte',amount:127.30},
    {date:'2026-02-19',desc:'UBER TRIP',                  person:'Alberto',cat:'Transporte',amount:57.91},
    {date:'2026-02-19',desc:'UBER TRIP',                  person:'Alberto',cat:'Transporte',amount:53.52},
    {date:'2026-02-22',desc:'UBER TRIP',                  person:'Alberto',cat:'Transporte',amount:69.08},
    {date:'2026-02-26',desc:'UBER TRIP',                  person:'Alberto',cat:'Transporte',amount:88.92},
    {date:'2026-03-01',desc:'UBER TRIP',                  person:'Alberto',cat:'Transporte',amount:169.76},
    {date:'2026-03-01',desc:'UBER TRIP',                  person:'Alberto',cat:'Transporte',amount:168.11},
    {date:'2026-02-04',desc:'PAYPAL *UBRPAGOSMEX',        person:'Alberto',cat:'Comida',    amount:88.97},
    {date:'2026-02-04',desc:'PAYPAL *UBRPAGOSMEX',        person:'Alberto',cat:'Comida',    amount:2430.41},
    {date:'2026-02-06',desc:'PAYPAL *UBRPAGOSMEX',        person:'Alberto',cat:'Comida',    amount:89.99},
    {date:'2026-02-07',desc:'PAYPAL *UBRPAGOSMEX',        person:'Alberto',cat:'Comida',    amount:607.34},
    {date:'2026-02-07',desc:'PAYPAL *UBRPAGOSMEX',        person:'Alberto',cat:'Comida',    amount:60.73},
    {date:'2026-02-07',desc:'PAYPAL *UBRPAGOSMEX',        person:'Alberto',cat:'Comida',    amount:10.00},
    {date:'2026-02-09',desc:'PAYPAL *UBRPAGOSMEX',        person:'Alberto',cat:'Comida',    amount:99.93},
    {date:'2026-02-14',desc:'PAYPAL *UBRPAGOSMEX',        person:'Alberto',cat:'Comida',    amount:836.12},
    {date:'2026-02-17',desc:'PAYPAL *UBRPAGOSMEX',        person:'Alberto',cat:'Comida',    amount:259.98},
    {date:'2026-02-19',desc:'PAYPAL *UBRPAGOSMEX',        person:'Alberto',cat:'Comida',    amount:479.98},
    {date:'2026-02-19',desc:'PAYPAL *UBRPAGOSMEX',        person:'Alberto',cat:'Comida',    amount:619.98},
    {date:'2026-02-22',desc:'PAYPAL *UBRPAGOSMEX',        person:'Alberto',cat:'Comida',    amount:1686.99},
    {date:'2026-02-22',desc:'PAYPAL *UBRPAGOSMEX',        person:'Alberto',cat:'Comida',    amount:168.70},
    {date:'2026-02-23',desc:'PAYPAL *UBRPAGOSMEX',        person:'Alberto',cat:'Comida',    amount:189.98},
    {date:'2026-02-23',desc:'PAYPAL *UBRPAGOSMEX',        person:'Alberto',cat:'Comida',    amount:10.00},
    {date:'2026-03-03',desc:'PAYPAL *UBRPAGOSMEX',        person:'Alberto',cat:'Comida',    amount:591.45},
    {date:'2026-03-03',desc:'PAYPAL *UBRPAGOSMEX',        person:'Alberto',cat:'Comida',    amount:74.00},
    {date:'2026-02-12',desc:'UBER EATS',                  person:'Alberto',cat:'Comida',    amount:18.00},
    {date:'2026-02-12',desc:'UBER EATS',                  person:'Alberto',cat:'Comida',    amount:188.50},
    {date:'2026-02-28',desc:'UBER EATS',                  person:'Alberto',cat:'Comida',    amount:187.71},
    {date:'2026-02-28',desc:'UBER EATS',                  person:'Alberto',cat:'Comida',    amount:14.64},
    {date:'2026-03-01',desc:'UBER EATS',                  person:'Alberto',cat:'Comida',    amount:40.84},
    {date:'2026-03-01',desc:'UBER EATS',                  person:'Alberto',cat:'Comida',    amount:412.88},
    {date:'2026-02-13',desc:'CITY MARKET SANTA FE 3',    person:'Alberto',cat:'Despensa',  amount:9828.24},
    {date:'2026-02-22',desc:'CITY MARKET SANTA FE 3',    person:'Alberto',cat:'Despensa',  amount:1897.96},
    {date:'2026-02-19',desc:'GASOL ES 05817 P5',          person:'Alberto',cat:'Gasolina',  amount:1210.18},
    {date:'2026-02-20',desc:'NETPAY*GAS URIBE 51',        person:'Alberto',cat:'Gasolina',  amount:2801.40},
    {date:'2026-02-20',desc:'MAPFRE TEPEYAC COBROS',      person:'Alberto',cat:'Salud',     amount:1395.20},
    {date:'2026-02-20',desc:'BUPA MEXICO',                person:'Alberto',cat:'Salud',     amount:20478.13},
    {date:'2026-02-28',desc:'STARLINK',                   person:'Alberto',cat:'Servicios', amount:255.00},
    {date:'2026-02-28',desc:'PAYPAL *LINKEDIN',           person:'Alberto',cat:'Servicios', amount:599.99},
    {date:'2026-03-01',desc:'PAYU-GOOGLE CLOUD',          person:'Alberto',cat:'Servicios', amount:4083.20},
    {date:'2026-03-01',desc:'PAYU-GOOGLE CLOUD',          person:'Alberto',cat:'Servicios', amount:221.11},
    {date:'2026-03-02',desc:'PAYPAL *SQUARESPACE',        person:'Alberto',cat:'Servicios', amount:513.00},
    {date:'2026-02-27',desc:'A1890BEUR01 MSBILL.INFO (€11.70)',person:'Alberto',cat:'Servicios',amount:244.74,foreign:true,currency:'EUR',foreignAmt:11.70},
    {date:'2026-02-25',desc:'GOOGLE *YOUTUBE PREMIUM ($8.50)',  person:'Alberto',cat:'Servicios',amount:149.89,foreign:true,currency:'USD',foreignAmt:8.50},
    {date:'2026-02-28',desc:'NETFLIX.COM ($24.99)',             person:'Alberto',cat:'Servicios',amount:441.92,foreign:true,currency:'USD',foreignAmt:24.99},
    {date:'2026-03-01',desc:'AT&T BILL PAYMENT ($295.29)',      person:'Alberto',cat:'Servicios',amount:5221.97,foreign:true,currency:'USD',foreignAmt:295.29},
    {date:'2026-02-06',desc:'MERCADO PAGO WALLET',        person:'Alberto',cat:'Compras',   amount:556.00},
    {date:'2026-02-09',desc:'AMAZON MX*AMAZON RETAIL',   person:'Alberto',cat:'Compras',   amount:99.00},
    {date:'2026-02-18',desc:'MERCADO PAGO WALLET',        person:'Alberto',cat:'Compras',   amount:6123.00},
    {date:'2026-03-01',desc:'HOTEL XCARET MEXICO',        person:'Alberto',cat:'Viajes',    amount:6845.52},
    {date:'2026-02-04',desc:'ROPA BOGGI SANTA FE (6/6 MSI)',   person:'Alberto',cat:'Ropa',      amount:2696.65,msi:true},
    {date:'2026-02-04',desc:'APPLE MEXICO (6/12 MSI)',         person:'Alberto',cat:'Tecnología',amount:966.50, msi:true},
    {date:'2026-02-04',desc:'APPLE MEXICO (6/12 MSI)',         person:'Alberto',cat:'Tecnología',amount:2791.59,msi:true},
    {date:'2026-02-04',desc:'MERCADO LIBRE (4/6 MSI)',         person:'Alberto',cat:'Compras',   amount:231.70, msi:true},
    {date:'2026-02-04',desc:'AMAZON MX (4/6 MSI)',             person:'Alberto',cat:'Compras',   amount:64.96,  msi:true},
    {date:'2026-02-04',desc:'AMAZON MX (4/6 MSI)',             person:'Alberto',cat:'Compras',   amount:64.96,  msi:true},
    {date:'2026-02-04',desc:'AMAZON MX (4/6 MSI)',             person:'Alberto',cat:'Compras',   amount:311.07, msi:true},
    {date:'2026-02-04',desc:'AMAZON MX (4/6 MSI)',             person:'Alberto',cat:'Compras',   amount:46.89,  msi:true},
    {date:'2026-02-04',desc:'AMAZON MX (4/6 MSI)',             person:'Alberto',cat:'Compras',   amount:1031.30,msi:true},
    {date:'2026-02-04',desc:"LEVI'S SANTA FE (3/3 MSI)",       person:'Alberto',cat:'Ropa',      amount:932.66, msi:true},
    {date:'2026-02-04',desc:'RAPSODIA SANTA FE (3/3 MSI)',     person:'Alberto',cat:'Ropa',      amount:1039.72,msi:true},
    {date:'2026-02-04',desc:'LIV SANTA FE (3/6 MSI)',          person:'Alberto',cat:'Restaurante',amount:2281.17,msi:true},
    {date:'2026-02-10',desc:'PAYPAL *CAMPER (1/12 MSI)',        person:'Alberto',cat:'Ropa',      amount:491.67, msi:true},
    {date:'2026-02-13',desc:'Gran Plan Benito Juárez',          person:'Lilia',  cat:'Restaurante',amount:6143.37},
    {date:'2026-02-13',desc:'AEROMEXICO',                       person:'Lilia',  cat:'Viajes',    amount:3270.00},
    {date:'2026-02-13',desc:'AEROMEXICO',                       person:'Lilia',  cat:'Viajes',    amount:812.00},
    {date:'2026-02-04',desc:'PLAN N TRAVEL (4/6 MSI)',          person:'Lilia',  cat:'Viajes',    amount:560.00, msi:true},
    {date:'2026-02-04',desc:'PLAN N TRAVEL (4/6 MSI)',          person:'Lilia',  cat:'Viajes',    amount:564.67, msi:true},
    {date:'2026-02-04',desc:'PLAN N TRAVEL (2/12 MSI)',         person:'Lilia',  cat:'Viajes',    amount:2203.09,msi:true},
    {date:'2026-02-05',desc:'EALDE 014300990 MADRID (€512)',    person:'Lilia',  cat:'Educación', amount:10836.31,foreign:true,currency:'EUR',foreignAmt:512},
    {date:'2026-03-03',desc:'PLAN DE PAGOS DIFERIDOS (6/6)',    person:'Lilia',  cat:'Diferidos', amount:23729.43,diferido:true},
    {date:'2026-03-03',desc:'PLAN DE PAGOS DIFERIDOS (5/6)',    person:'Lilia',  cat:'Diferidos', amount:14296.21,diferido:true},
    {date:'2026-03-03',desc:'PLAN DE PAGOS DIFERIDOS (3/6)',    person:'Lilia',  cat:'Diferidos', amount:13095.34,diferido:true},
    {date:'2026-03-03',desc:'PLAN DE PAGOS DIFERIDOS (2/6)',    person:'Lilia',  cat:'Diferidos', amount:28205.34,diferido:true},
    {date:'2026-03-03',desc:'PLAN DE PAGOS DIFERIDOS (1/6)',    person:'Lilia',  cat:'Diferidos', amount:28209.72,diferido:true},
  ],
  msiPlans:[
    {id:'m1', desc:'ROPA BOGGI SANTA FE',      person:'Alberto',origDate:'2025-09-04',origAmt:16180.00, totalMonths:6,  currentMonth:6, pending:0,        monthly:2696.65},
    {id:'m2', desc:'APPLE MEXICO',             person:'Alberto',origDate:'2025-09-11',origAmt:11598.00, totalMonths:12, currentMonth:6, pending:5799.00,  monthly:966.50},
    {id:'m3', desc:'APPLE MEXICO',             person:'Alberto',origDate:'2025-09-15',origAmt:33499.00, totalMonths:12, currentMonth:6, pending:16749.46, monthly:2791.59},
    {id:'m4', desc:'PLAN N TRAVEL SERVICES',   person:'Lilia',  origDate:'2025-11-17',origAmt:3360.00,  totalMonths:6,  currentMonth:4, pending:1120.00,  monthly:560.00},
    {id:'m5', desc:'PLAN N TRAVEL SERVICES',   person:'Lilia',  origDate:'2025-11-17',origAmt:3388.00,  totalMonths:6,  currentMonth:4, pending:1129.32,  monthly:564.67},
    {id:'m6', desc:'MERCADO LIBRE',            person:'Alberto',origDate:'2025-11-28',origAmt:1390.20,  totalMonths:6,  currentMonth:4, pending:463.40,   monthly:231.70},
    {id:'m7', desc:'AMAZON MX',                person:'Alberto',origDate:'2025-11-29',origAmt:389.73,   totalMonths:6,  currentMonth:4, pending:129.89,   monthly:64.96},
    {id:'m8', desc:'AMAZON MX',                person:'Alberto',origDate:'2025-11-29',origAmt:389.73,   totalMonths:6,  currentMonth:4, pending:129.89,   monthly:64.96},
    {id:'m9', desc:'AMAZON MX',                person:'Alberto',origDate:'2025-11-29',origAmt:1866.40,  totalMonths:6,  currentMonth:4, pending:622.12,   monthly:311.07},
    {id:'m10',desc:'AMAZON MX',                person:'Alberto',origDate:'2025-11-29',origAmt:281.31,   totalMonths:6,  currentMonth:4, pending:93.75,    monthly:46.89},
    {id:'m11',desc:'AMAZON MX',                person:'Alberto',origDate:'2025-12-01',origAmt:6187.80,  totalMonths:6,  currentMonth:4, pending:2062.60,  monthly:1031.30},
    {id:'m12',desc:"LEVI'S SANTA FE",          person:'Alberto',origDate:'2025-12-15',origAmt:2798.00,  totalMonths:3,  currentMonth:3, pending:0,        monthly:932.66},
    {id:'m13',desc:'RAPSODIA SANTA FE',        person:'Alberto',origDate:'2025-12-23',origAmt:3119.20,  totalMonths:3,  currentMonth:3, pending:0,        monthly:1039.72},
    {id:'m14',desc:'LIV SANTA FE',             person:'Alberto',origDate:'2025-12-29',origAmt:13687.00, totalMonths:6,  currentMonth:3, pending:6843.49,  monthly:2281.17},
    {id:'m15',desc:'PLAN N TRAVEL (12 meses)', person:'Lilia',  origDate:'2026-01-29',origAmt:26437.04, totalMonths:12, currentMonth:2, pending:22030.86, monthly:2203.09},
    {id:'m16',desc:'PAYPAL *CAMPER',           person:'Alberto',origDate:'2026-02-08',origAmt:5900.00,  totalMonths:12, currentMonth:1, pending:5408.33,  monthly:491.67},
  ],
  pagosDiferidos:[
    {id:'d1',origDate:'2025-09-15',montoDiferido:117783.62,comision:3.0,totalMonths:6,currentMonth:6,pending:0,        monthly:23729.43},
    {id:'d2',origDate:'2025-10-15',montoDiferido:70960.65, comision:3.0,totalMonths:6,currentMonth:5,pending:11826.75, monthly:14296.21},
    {id:'d3',origDate:'2025-12-12',montoDiferido:65000.00, comision:3.0,totalMonths:6,currentMonth:3,pending:32499.98, monthly:13095.34},
    {id:'d4',origDate:'2026-01-16',montoDiferido:140000.00,comision:3.0,totalMonths:6,currentMonth:2,pending:93333.32, monthly:28205.34},
    {id:'d5',origDate:'2026-02-16',montoDiferido:140021.77,comision:3.0,totalMonths:6,currentMonth:1,pending:116684.80,monthly:28209.72},
  ],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt  = n => (n??0).toLocaleString('es-MX',{minimumFractionDigits:2,maximumFractionDigits:2});
const fmtK = n => n>=1000 ? `${(n/1000).toFixed(0)}k` : fmt(n);
const MNAMES = ['','Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
function fmtDate(s){ if(!s)return'—'; const d=new Date(s+'T12:00:00'); return `${d.getDate()} ${MNAMES[d.getMonth()+1]} ${d.getFullYear()}`; }
function ProgressBar({current,total,color='#10b981'}){
  const pct=Math.min(100,Math.round((current/total)*100));
  return <div style={{height:6,background:'var(--bg-tertiary)',borderRadius:4,overflow:'hidden',marginTop:'0.4rem'}}>
    <div style={{height:'100%',width:`${pct}%`,background:color,borderRadius:4}}/>
  </div>;
}

// ─── Tendencias View ──────────────────────────────────────────────────────────
function TendenciasView({ statements, onBack }) {
  // oldest → newest
  const sorted = useMemo(() => [...statements].sort((a,b)=>a.id.localeCompare(b.id)), [statements]);

  // Category totals per statement (excluding Diferidos)
  const catData = useMemo(() => {
    const allCats = new Set();
    const rows = sorted.map(s => {
      const t = {};
      (s.charges||[]).forEach(c => { if(c.diferido)return; t[c.cat]=(t[c.cat]||0)+c.amount; allCats.add(c.cat); });
      const total = Object.entries(t).reduce((s,[k,v])=>k==='Diferidos'?s:s+v,0);
      const msiPending = (s.msiPlans||[]).reduce((s,p)=>s+p.pending,0);
      const difPending = (s.pagosDiferidos||[]).reduce((s,p)=>s+p.pending,0);
      return { label:s.label, id:s.id, totals:t, total, msiPending, difPending };
    });
    return { rows, allCats: [...allCats].filter(k=>k!=='Diferidos') };
  }, [sorted]);

  const maxTotal = Math.max(...catData.rows.map(r=>r.total), 1);
  const maxMsi   = Math.max(...catData.rows.map(r=>r.msiPending), 1);
  const maxDif   = Math.max(...catData.rows.map(r=>r.difPending), 1);

  const SectionTitle = ({children}) => (
    <div style={{fontSize:'0.82rem',fontWeight:700,color:'var(--text-primary)',padding:'0.75rem 0 0.5rem',borderBottom:'1px solid var(--border-color)',marginBottom:'0.75rem'}}>
      {children}
    </div>
  );

  const MultiBar = ({ catKey }) => {
    const cfg = CATS[catKey]||{emoji:'📌',color:'#6b7280'};
    const vals = catData.rows.map(r=>({label:r.label,val:r.totals[catKey]||0}));
    const catMax = Math.max(...vals.map(v=>v.val),1);
    // month-over-month delta (last two)
    let delta = null;
    if (vals.length >= 2) {
      const a = vals[vals.length-2].val, b = vals[vals.length-1].val;
      if (a>0) delta = { pct: ((b-a)/a*100).toFixed(0), up: b>a };
    }
    return (
      <div style={{marginBottom:'0.875rem',border:'1px solid var(--border-color)',borderRadius:'0.75rem',overflow:'hidden'}}>
        <div style={{background:'var(--bg-secondary)',padding:'0.6rem 0.875rem',display:'flex',alignItems:'center',gap:'0.5rem'}}>
          <span style={{fontSize:'1rem'}}>{cfg.emoji}</span>
          <span style={{flex:1,fontWeight:700,fontSize:'0.85rem',color:'var(--text-primary)'}}>{catKey}</span>
          {delta && <span style={{fontSize:'0.72rem',fontWeight:700,color:delta.up?'#ef4444':'#10b981'}}>
            {delta.up?'↑':'↓'} {Math.abs(delta.pct)}% vs anterior
          </span>}
        </div>
        <div style={{padding:'0.5rem 0.875rem',background:'var(--bg-primary)',display:'flex',flexDirection:'column',gap:'0.35rem'}}>
          {vals.map(({label,val})=>(
            <div key={label} style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
              <span style={{width:'4rem',fontSize:'0.7rem',color:'var(--text-secondary)',flexShrink:0}}>{label}</span>
              <div style={{flex:1,height:14,background:'var(--bg-tertiary)',borderRadius:3,overflow:'hidden'}}>
                <div style={{height:'100%',width:`${(val/catMax)*100}%`,background:cfg.color,borderRadius:3}}/>
              </div>
              <span style={{width:'4.5rem',fontSize:'0.7rem',fontWeight:700,color:cfg.color,textAlign:'right',flexShrink:0}}>${fmtK(val)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="page-container">
      {/* Header */}
      <div style={{display:'flex',alignItems:'center',gap:'0.75rem',marginBottom:'1.25rem'}}>
        <button onClick={onBack} style={{background:'none',border:'1px solid var(--border-color)',borderRadius:'0.5rem',padding:'0.4rem',cursor:'pointer',color:'var(--text-secondary)',display:'flex',alignItems:'center'}}>
          <ArrowLeft size={18}/>
        </button>
        <h1 className="page-title" style={{margin:0}}>Tendencias AMEX</h1>
      </div>

      {sorted.length < 2 && (
        <div style={{background:'rgba(99,102,241,0.08)',border:'1px solid rgba(99,102,241,0.2)',borderRadius:'0.75rem',padding:'1rem',marginBottom:'1rem',fontSize:'0.82rem',color:'var(--text-secondary)'}}>
          💡 Agrega más estados de cuenta (usa el botón Importar en la vista principal) para ver tendencias mes a mes.
        </div>
      )}

      {/* 4 KPI Summary Cards (latest statement) */}
      {sorted.length > 0 && (() => {
        const last = sorted[sorted.length - 1];
        const msiP  = (last.msiPlans||[]).reduce((s,p)=>s+(p.pending||0),0);
        const difP  = (last.pagosDiferidos||[]).filter(d=>(d.pending||0)>0).reduce((s,d)=>s+(d.pending||0),0);
        const gastos = (last.charges||[]).filter(c=>!c.diferido).reduce((s,c)=>s+c.amount,0);
        return (
          <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:'0.5rem',marginBottom:'1.25rem'}}>
            {[
              {l:'Saldo a Pagar',  v:last.balance||0, c:'#ef4444', emoji:'💳'},
              {l:'Total Gastos',   v:gastos,           c:'var(--text-primary)', emoji:'📊'},
              {l:'MSI Pendiente',  v:msiP,             c:'#6366f1', emoji:'📅'},
              {l:'Dif. Pendiente', v:difP,             c:'#f97316', emoji:'📋'},
            ].map(({l,v,c,emoji})=>(
              <div key={l} style={{background:'var(--bg-secondary)',border:'1px solid var(--border-color)',borderRadius:'0.75rem',padding:'0.75rem 0.875rem',textAlign:'center'}}>
                <div style={{fontSize:'0.62rem',color:'var(--text-secondary)',marginBottom:'0.15rem'}}>{emoji} {l}</div>
                <div style={{fontSize:'1rem',fontWeight:800,color:c}}>${fmt(v)}</div>
                <div style={{fontSize:'0.6rem',color:'var(--text-light)',marginTop:'0.1rem'}}>{last.label}</div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Total gastos per month */}
      <SectionTitle>💳 Total de Gastos por Mes</SectionTitle>
      <div style={{marginBottom:'1rem',border:'1px solid var(--border-color)',borderRadius:'0.75rem',overflow:'hidden'}}>
        <div style={{padding:'0.625rem 0.875rem',background:'var(--bg-primary)',display:'flex',flexDirection:'column',gap:'0.35rem'}}>
          {catData.rows.map(({label,total})=>(
            <div key={label} style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
              <span style={{width:'4rem',fontSize:'0.72rem',color:'var(--text-secondary)',flexShrink:0}}>{label}</span>
              <div style={{flex:1,height:18,background:'var(--bg-tertiary)',borderRadius:3,overflow:'hidden'}}>
                <div style={{height:'100%',width:`${(total/maxTotal)*100}%`,background:'#1a472a',borderRadius:3}}/>
              </div>
              <span style={{width:'5.5rem',fontSize:'0.78rem',fontWeight:700,color:'var(--text-primary)',textAlign:'right',flexShrink:0}}>${fmt(total)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Per-category bars */}
      <SectionTitle>📊 Gastos por Categoría</SectionTitle>
      {catData.allCats.map(cat => <MultiBar key={cat} catKey={cat}/>)}

      {/* MSI pending trend */}
      {catData.rows.some(r=>r.msiPending>0) && (
        <>
          <SectionTitle>📅 Saldo Pendiente – Meses sin Intereses</SectionTitle>
          <div style={{marginBottom:'1rem',border:'1px solid var(--border-color)',borderRadius:'0.75rem',overflow:'hidden'}}>
            <div style={{padding:'0.625rem 0.875rem',background:'var(--bg-primary)',display:'flex',flexDirection:'column',gap:'0.35rem'}}>
              {catData.rows.map(({label,msiPending})=>(
                <div key={label} style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
                  <span style={{width:'4rem',fontSize:'0.7rem',color:'var(--text-secondary)',flexShrink:0}}>{label}</span>
                  <div style={{flex:1,height:14,background:'var(--bg-tertiary)',borderRadius:3,overflow:'hidden'}}>
                    <div style={{height:'100%',width:`${(msiPending/maxMsi)*100}%`,background:'#6366f1',borderRadius:3}}/>
                  </div>
                  <span style={{width:'4.5rem',fontSize:'0.7rem',fontWeight:700,color:'#6366f1',textAlign:'right',flexShrink:0}}>${fmtK(msiPending)}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Diferidos pending trend */}
      {catData.rows.some(r=>r.difPending>0) && (
        <>
          <SectionTitle>📋 Saldo Pendiente – Pagos Diferidos</SectionTitle>
          <div style={{marginBottom:'1rem',border:'1px solid var(--border-color)',borderRadius:'0.75rem',overflow:'hidden'}}>
            <div style={{padding:'0.625rem 0.875rem',background:'var(--bg-primary)',display:'flex',flexDirection:'column',gap:'0.35rem'}}>
              {catData.rows.map(({label,difPending})=>(
                <div key={label} style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
                  <span style={{width:'4rem',fontSize:'0.7rem',color:'var(--text-secondary)',flexShrink:0}}>{label}</span>
                  <div style={{flex:1,height:14,background:'var(--bg-tertiary)',borderRadius:3,overflow:'hidden'}}>
                    <div style={{height:'100%',width:`${(difPending/maxDif)*100}%`,background:'#ef4444',borderRadius:3}}/>
                  </div>
                  <span style={{width:'4.5rem',fontSize:'0.7rem',fontWeight:700,color:'#ef4444',textAlign:'right',flexShrink:0}}>${fmtK(difPending)}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Extract text from a PDF File using PDF.js ───────────────────────────────
function readFileAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(e.target.error);
    reader.readAsArrayBuffer(file);
  });
}

async function extractPdfText(file) {
  const arrayBuffer = await readFileAsArrayBuffer(file);
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  let fullText = '';
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    // Group items by approximate Y position to preserve line structure
    const byLine = {};
    content.items.forEach(item => {
      const y = Math.round(item.transform[5]);
      if (!byLine[y]) byLine[y] = [];
      byLine[y].push(item.str);
    });
    const lines = Object.keys(byLine)
      .sort((a, b) => b - a) // top-to-bottom (Y decreases downward in PDF)
      .map(y => byLine[y].join(' ').trim())
      .filter(Boolean);
    fullText += lines.join('\n') + '\n';
  }
  return fullText;
}

// ─── Import View ──────────────────────────────────────────────────────────────
function ImportView({ db, onBack, onSaved }) {
  const [preview,   setPreview]   = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [parsing,   setParsing]   = useState(false);
  const [fileName,  setFileName]  = useState('');
  const [dragOver,  setDragOver]  = useState(false);
  const fileInputRef = useRef(null);

  const processPdf = async (file) => {
    if (!file || file.type !== 'application/pdf') {
      showToast('Por favor selecciona un archivo PDF', 'error'); return;
    }
    setFileName(file.name);
    setParsing(true);
    setPreview(null);
    try {
      const text = await extractPdfText(file);
      const result = parseAmexText(text);
      if (!result.cutDate) {
        showToast('No se detectó el período. Verifica que el PDF es un estado AMEX válido.', 'error');
      } else {
        setPreview(result);
      }
    } catch (err) {
      showToast('Error leyendo el PDF: ' + err.message, 'error');
    } finally {
      setParsing(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) processPdf(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processPdf(file);
  };

  const handleSave = async () => {
    if (!preview) return;
    setSaving(true);
    try {
      await setDoc(doc(db, 'amexStatements', preview.id), { ...preview, createdAt: serverTimestamp() });
      showToast(`Estado ${preview.label} guardado (${preview.charges.length} cargos detectados)`);
      onSaved(preview);
    } catch(err) { showToast('Error: ' + err.message, 'error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="page-container">
      <div style={{display:'flex',alignItems:'center',gap:'0.75rem',marginBottom:'1.25rem'}}>
        <button onClick={onBack} style={{background:'none',border:'1px solid var(--border-color)',borderRadius:'0.5rem',padding:'0.4rem',cursor:'pointer',color:'var(--text-secondary)',display:'flex',alignItems:'center'}}>
          <ArrowLeft size={18}/>
        </button>
        <h1 className="page-title" style={{margin:0}}>Importar Estado AMEX</h1>
      </div>

      {/* Drop zone */}
      <div
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        style={{
          border: `2px dashed ${dragOver ? '#1a472a' : 'var(--border-color)'}`,
          borderRadius: '0.875rem',
          padding: '2.5rem 1rem',
          textAlign: 'center',
          cursor: 'pointer',
          background: dragOver ? 'rgba(26,71,42,0.08)' : 'var(--bg-secondary)',
          transition: 'all 0.2s',
          marginBottom: '1rem',
        }}>
        <input ref={fileInputRef} type="file" accept="application/pdf" onChange={handleFileChange} style={{display:'none'}}/>
        {parsing ? (
          <>
            <div className="spinner" style={{margin:'0 auto 0.75rem'}}/>
            <div style={{fontWeight:600,color:'var(--text-primary)'}}>Leyendo PDF…</div>
            <div style={{fontSize:'0.75rem',color:'var(--text-secondary)',marginTop:'0.25rem'}}>{fileName}</div>
          </>
        ) : fileName && !preview ? (
          <>
            <Upload size={28} style={{color:'#ef4444',marginBottom:'0.5rem'}}/>
            <div style={{fontWeight:600,color:'#ef4444'}}>No se pudo detectar el período</div>
            <div style={{fontSize:'0.75rem',color:'var(--text-secondary)',marginTop:'0.25rem'}}>Toca para intentar con otro archivo</div>
          </>
        ) : (
          <>
            <Upload size={28} style={{color:dragOver?'#1a472a':'var(--text-light)',marginBottom:'0.5rem'}}/>
            <div style={{fontWeight:700,color:'var(--text-primary)',fontSize:'0.95rem'}}>
              {fileName && preview ? `✅ ${fileName}` : 'Selecciona o arrastra el PDF'}
            </div>
            <div style={{fontSize:'0.75rem',color:'var(--text-secondary)',marginTop:'0.25rem'}}>
              Estado de cuenta AMEX en formato PDF
            </div>
          </>
        )}
      </div>

      {/* Preview */}
      {preview && (
        <div style={{border:'1px solid var(--border-color)',borderRadius:'0.75rem',overflow:'hidden',marginBottom:'1rem'}}>
          <div style={{background:'var(--bg-secondary)',padding:'0.875rem',borderBottom:'1px solid var(--border-color)'}}>
            <div style={{fontWeight:700,fontSize:'0.9rem',color:'var(--text-primary)',marginBottom:'0.25rem'}}>
              ✅ Período detectado: <span style={{color:'#10b981'}}>{preview.label}</span>
            </div>
            <div style={{fontSize:'0.75rem',color:'var(--text-secondary)'}}>
              {preview.period} · Vence {fmtDate(preview.dueDate)}
            </div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:'0.5rem',padding:'0.875rem'}}>
            {[
              ['Saldo a pagar', preview.balance, '#ef4444'],
              ['Nuevos cargos', preview.newCharges, 'var(--text-primary)'],
              ['Pagos/Créditos',preview.payments, '#10b981'],
              ['Comisiones',   preview.commissions,'#f59e0b'],
            ].map(([l,v,c])=>(
              <div key={l} style={{background:'var(--bg-tertiary)',borderRadius:'0.5rem',padding:'0.5rem 0.625rem'}}>
                <div style={{fontSize:'0.68rem',color:'var(--text-secondary)'}}>{l}</div>
                <div style={{fontWeight:700,fontSize:'0.9rem',color:c}}>${fmt(v)}</div>
              </div>
            ))}
          </div>
          <div style={{padding:'0 0.875rem 0.875rem',fontSize:'0.78rem',color:'var(--text-secondary)'}}>
            {preview.charges.length > 0
              ? `${preview.charges.length} cargos detectados y auto-categorizados`
              : '⚠️ No se detectaron cargos individuales — solo se guardará el resumen'}
          </div>
          <div style={{padding:'0 0.875rem 0.875rem'}}>
            <button onClick={handleSave} disabled={saving}
              style={{width:'100%',padding:'0.625rem',border:'none',borderRadius:'0.5rem',background:'#1a472a',color:'white',fontWeight:700,cursor:saving?'not-allowed':'pointer',opacity:saving?0.7:1}}>
              {saving ? 'Guardando…' : `Guardar estado ${preview.label}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function AmexEstados({ db }) {
  const [statements, setStatements] = useState([]);
  const [selIdx, setSelIdx]         = useState(0);
  const [view, setView]             = useState('detail');  // 'detail'|'tendencias'|'import'
  const [tab, setTab]               = useState('gastos');
  const [personFilter, setPersonFilter] = useState('Todos');
  const [expandedCats, setExpandedCats] = useState({});
  const [showMsi, setShowMsi]           = useState('all');

  // Load / seed
  useEffect(() => {
    if (!db) return;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'amexStatements'));
        let loaded = snap.docs.map(d => ({ id:d.id, ...d.data() }));
        if (!loaded.find(s => s.id === SEED.id)) {
          await setDoc(doc(db, 'amexStatements', SEED.id), { ...SEED, createdAt:serverTimestamp() });
          loaded = [SEED, ...loaded];
        }
        loaded.sort((a,b) => a.id.localeCompare(b.id)); // oldest first
        setStatements(loaded);
        setSelIdx(loaded.length - 1); // start on newest
      } catch(err) {
        console.error(err);
        setStatements([SEED]);
        setSelIdx(0);
      }
    })();
  }, [db]);

  const selected  = statements[selIdx];
  const canPrev   = selIdx > 0;
  const canNext   = selIdx < statements.length - 1;

  const addStatement = useCallback((s) => {
    setStatements(prev => {
      const next = [...prev.filter(x => x.id !== s.id), s].sort((a,b)=>a.id.localeCompare(b.id));
      setSelIdx(next.findIndex(x => x.id === s.id));
      return next;
    });
    setView('detail');
  }, []);

  // Gastos derived
  const charges = useMemo(() => {
    if (!selected?.charges) return [];
    return selected.charges.filter(c => personFilter==='Todos' || c.person===personFilter);
  }, [selected, personFilter]);

  const byCategory = useMemo(() => {
    const map = {};
    charges.forEach(c => {
      if (c.diferido) return;
      if (!map[c.cat]) map[c.cat] = { total:0, items:[] };
      map[c.cat].total += c.amount;
      map[c.cat].items.push(c);
    });
    return Object.entries(map).sort((a,b)=>b[1].total-a[1].total);
  }, [charges]);

  const totalGastos = useMemo(() => byCategory.reduce((s,[,v])=>s+v.total,0), [byCategory]);

  const msiActive = (selected?.msiPlans??[]).filter(p=>p.pending>0);
  const msiDone   = (selected?.msiPlans??[]).filter(p=>p.pending===0);
  const msiShow   = showMsi==='all'?(selected?.msiPlans??[]):showMsi==='active'?msiActive:msiDone;
  const totalMsiPending = msiActive.reduce((s,p)=>s+p.pending,0);
  const totalDifPending = (selected?.pagosDiferidos??[]).filter(d=>d.pending>0).reduce((s,d)=>s+d.pending,0);
  const totalDifMonthly = (selected?.pagosDiferidos??[]).filter(d=>d.pending>0).reduce((s,d)=>s+d.monthly,0);

  if (view === 'tendencias') return <TendenciasView statements={statements} onBack={()=>setView('detail')}/>;
  if (view === 'import')     return <ImportView db={db} onBack={()=>setView('detail')} onSaved={addStatement}/>;
  if (!selected) return <div className="page-container"><div className="app-loading" style={{minHeight:'30vh'}}><div className="spinner"/></div></div>;

  const tabBtn = (key, lbl) => (
    <button key={key} onClick={()=>setTab(key)}
      style={{flex:1,padding:'0.45rem 0.25rem',border:'none',cursor:'pointer',fontWeight:700,fontSize:'0.72rem',
        background:tab===key?'#1a472a':'transparent',color:tab===key?'white':'var(--text-secondary)',
        borderRadius:'0.5rem',whiteSpace:'nowrap'}}>
      {lbl}
    </button>
  );
  const pBtn = (val,lbl) => (
    <button key={val} onClick={()=>setPersonFilter(val)}
      style={{padding:'0.3rem 0.75rem',border:`1px solid ${personFilter===val?'#1a472a':'var(--border-color)'}`,
        borderRadius:'0.5rem',background:personFilter===val?'#1a472a':'var(--bg-tertiary)',
        color:personFilter===val?'white':'var(--text-secondary)',cursor:'pointer',fontSize:'0.78rem',fontWeight:600}}>
      {lbl}
    </button>
  );

  return (
    <div className="page-container">
      {/* Title */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'1rem',gap:'0.5rem'}}>
        <h1 className="page-title" style={{margin:0}}>
          AMEX
          <span style={{fontSize:'0.7rem',background:'rgba(16,185,129,0.12)',color:'#10b981',borderRadius:'0.35rem',padding:'0.2rem 0.5rem',fontWeight:700,verticalAlign:'middle',marginLeft:'0.5rem'}}>MXN</span>
        </h1>
        <div style={{display:'flex',gap:'0.4rem'}}>
          <button onClick={()=>setView('import')}
            style={{display:'flex',alignItems:'center',gap:'0.3rem',padding:'0.4rem 0.75rem',border:'1px solid var(--border-color)',borderRadius:'0.5rem',background:'var(--bg-tertiary)',color:'var(--text-secondary)',cursor:'pointer',fontSize:'0.78rem',fontWeight:600}}>
            <Upload size={13}/> Importar
          </button>
          <button onClick={()=>setView('tendencias')}
            style={{display:'flex',alignItems:'center',gap:'0.3rem',padding:'0.4rem 0.75rem',border:'1px solid var(--border-color)',borderRadius:'0.5rem',background:'var(--bg-tertiary)',color:'var(--text-secondary)',cursor:'pointer',fontSize:'0.78rem',fontWeight:600}}>
            <TrendingUp size={13}/> Tendencias
          </button>
        </div>
      </div>

      {/* Arrow navigation */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'0.75rem',marginBottom:'1rem'}}>
        <button onClick={()=>setSelIdx(i=>i-1)} disabled={!canPrev}
          style={{padding:'0.4rem',border:'1px solid var(--border-color)',borderRadius:'0.5rem',background:'var(--bg-tertiary)',cursor:canPrev?'pointer':'default',color:canPrev?'var(--text-primary)':'var(--text-light)',display:'flex',alignItems:'center',opacity:canPrev?1:0.35}}>
          <ChevronLeft size={18}/>
        </button>
        <div style={{textAlign:'center',minWidth:'7rem'}}>
          <div style={{fontWeight:800,fontSize:'1.1rem',color:'var(--text-primary)'}}>{selected.label}</div>
          <div style={{fontSize:'0.68rem',color:'var(--text-secondary)'}}>{selected.period}</div>
        </div>
        <button onClick={()=>setSelIdx(i=>i+1)} disabled={!canNext}
          style={{padding:'0.4rem',border:'1px solid var(--border-color)',borderRadius:'0.5rem',background:'var(--bg-tertiary)',cursor:canNext?'pointer':'default',color:canNext?'var(--text-primary)':'var(--text-light)',display:'flex',alignItems:'center',opacity:canNext?1:0.35}}>
          <ChevronRight size={18}/>
        </button>
      </div>

      {/* Summary card */}
      <div className="card" style={{padding:'1rem',marginBottom:'1rem'}}>
        <div style={{display:'flex',alignItems:'center',gap:'0.5rem',marginBottom:'0.875rem'}}>
          <CreditCard size={16} style={{color:'var(--text-secondary)'}}/>
          <div style={{flex:1}}>
            <div style={{fontWeight:700,fontSize:'0.9rem',color:'var(--text-primary)'}}>The Platinum Card – Aeroméxico</div>
            <div style={{fontSize:'0.72rem',color:'var(--text-secondary)'}}>Vence {fmtDate(selected.dueDate)}</div>
          </div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:'0.5rem'}}>
          {[
            {l:'Saldo a pagar', v:selected.balance,    c:'#ef4444',bold:true},
            {l:'Nuevos cargos', v:selected.newCharges, c:'var(--text-primary)'},
            {l:'Pagos/Créditos',v:selected.payments,   c:'#10b981'},
            {l:'Comisiones',    v:selected.commissions,c:'#f59e0b'},
          ].map(({l,v,c,bold})=>(
            <div key={l} style={{background:'var(--bg-tertiary)',borderRadius:'0.5rem',padding:'0.5rem 0.625rem'}}>
              <div style={{fontSize:'0.68rem',color:'var(--text-secondary)'}}>{l}</div>
              <div style={{fontSize:'0.9rem',fontWeight:bold?800:600,color:c}}>${fmt(v)}</div>
            </div>
          ))}
        </div>
        <div style={{display:'flex',gap:'0.5rem',marginTop:'0.5rem'}}>
          <div style={{flex:1,background:'rgba(99,102,241,0.08)',border:'1px solid rgba(99,102,241,0.2)',borderRadius:'0.5rem',padding:'0.4rem 0.625rem'}}>
            <div style={{fontSize:'0.65rem',color:'#6366f1',fontWeight:600}}>Pendiente MSI</div>
            <div style={{fontSize:'0.85rem',fontWeight:700,color:'#6366f1'}}>${fmt(totalMsiPending)}</div>
          </div>
          <div style={{flex:1,background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:'0.5rem',padding:'0.4rem 0.625rem'}}>
            <div style={{fontSize:'0.65rem',color:'#ef4444',fontWeight:600}}>Pendiente Diferidos</div>
            <div style={{fontSize:'0.85rem',fontWeight:700,color:'#ef4444'}}>${fmt(totalDifPending)}</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{display:'flex',gap:'0.2rem',background:'var(--bg-tertiary)',borderRadius:'0.75rem',padding:'0.25rem',marginBottom:'1rem'}}>
        {tabBtn('gastos','💳 Gastos')}
        {tabBtn('msi','📅 Sin Intereses')}
        {tabBtn('diferidos','📋 Diferidos')}
      </div>

      {/* ── GASTOS ── */}
      {tab==='gastos' && (
        <>
          <div style={{display:'flex',gap:'0.4rem',marginBottom:'0.75rem',flexWrap:'wrap'}}>
            {pBtn('Todos','Todos')}{pBtn('Alberto','Alberto')}{pBtn('Lilia','Lilia')}
          </div>
          <div style={{fontSize:'0.78rem',color:'var(--text-secondary)',marginBottom:'0.625rem'}}>
            Total: <strong style={{color:'var(--text-primary)'}}>${fmt(totalGastos)}</strong>
            <span style={{marginLeft:'0.4rem',fontSize:'0.7rem'}}>(incl. MSI y diferidos)</span>
          </div>
          {byCategory.map(([cat,{total,items}])=>{
            const cfg=CATS[cat]||{emoji:'📌',color:'#6b7280'};
            const pct=totalGastos>0?(total/totalGastos)*100:0;
            const isOpen=expandedCats[cat];
            return (
              <div key={cat} style={{marginBottom:'0.5rem',border:'1px solid var(--border-color)',borderRadius:'0.75rem',overflow:'hidden'}}>
                <button onClick={()=>setExpandedCats(p=>({...p,[cat]:!p[cat]}))}
                  style={{width:'100%',background:'var(--bg-secondary)',border:'none',padding:'0.75rem 0.875rem',cursor:'pointer',display:'flex',alignItems:'center',gap:'0.625rem',textAlign:'left'}}>
                  <span style={{fontSize:'1.1rem',flexShrink:0}}>{cfg.emoji}</span>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <span style={{fontWeight:700,fontSize:'0.85rem',color:'var(--text-primary)'}}>{cat}</span>
                      <span style={{fontWeight:700,fontSize:'0.9rem',color:cfg.color}}>${fmt(total)}</span>
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:'0.5rem',marginTop:'0.2rem'}}>
                      <div style={{flex:1,height:4,background:'var(--bg-tertiary)',borderRadius:2,overflow:'hidden'}}>
                        <div style={{height:'100%',width:`${pct}%`,background:cfg.color,borderRadius:2}}/>
                      </div>
                      <span style={{fontSize:'0.65rem',color:'var(--text-light)',flexShrink:0}}>{pct.toFixed(1)}%</span>
                      <span style={{fontSize:'0.65rem',color:'var(--text-light)',flexShrink:0}}>{items.length} cargo{items.length!==1?'s':''}</span>
                    </div>
                  </div>
                  {isOpen?<ChevronUp size={14} style={{color:'var(--text-light)',flexShrink:0}}/>:<ChevronDown size={14} style={{color:'var(--text-light)',flexShrink:0}}/>}
                </button>
                {isOpen && (
                  <div style={{background:'var(--bg-primary)'}}>
                    {items.map((c,i)=>(
                      <div key={i} style={{display:'flex',alignItems:'center',gap:'0.5rem',padding:'0.45rem 0.875rem',borderTop:'1px solid var(--border-color)'}}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:'0.78rem',color:'var(--text-primary)',wordBreak:'break-word'}}>
                            {c.desc}
                            {c.msi && <span style={{marginLeft:'0.3rem',fontSize:'0.6rem',background:'rgba(99,102,241,0.12)',color:'#6366f1',padding:'0.05rem 0.3rem',borderRadius:'0.2rem',fontWeight:700}}>MSI</span>}
                            {c.foreign && <span style={{marginLeft:'0.3rem',fontSize:'0.6rem',background:'rgba(245,158,11,0.12)',color:'#f59e0b',padding:'0.05rem 0.3rem',borderRadius:'0.2rem',fontWeight:700}}>{c.currency}</span>}
                          </div>
                          <div style={{fontSize:'0.67rem',color:'var(--text-light)'}}>{fmtDate(c.date)} · {c.person}</div>
                        </div>
                        <div style={{fontWeight:600,fontSize:'0.85rem',color:'var(--text-primary)',flexShrink:0}}>${fmt(c.amount)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {/* ── MSI ── */}
      {tab==='msi' && (
        <>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'0.5rem',marginBottom:'0.75rem'}}>
            {[
              {l:'Activos',      v:msiActive.length,                                        c:'#6366f1'},
              {l:'Pago mensual', v:`$${fmt(msiActive.reduce((s,p)=>s+p.monthly,0))}`,       c:'#3b82f6'},
              {l:'Pendiente',    v:`$${fmt(totalMsiPending)}`,                               c:'#ef4444'},
            ].map(({l,v,c})=>(
              <div key={l} style={{background:'var(--bg-tertiary)',borderRadius:'0.625rem',padding:'0.5rem',textAlign:'center'}}>
                <div style={{fontSize:'0.62rem',color:'var(--text-secondary)'}}>{l}</div>
                <div style={{fontSize:'0.88rem',fontWeight:700,color:c}}>{v}</div>
              </div>
            ))}
          </div>
          <div style={{display:'flex',gap:'0.4rem',marginBottom:'0.75rem'}}>
            {[['all','Todos'],['active','Activos'],['done','Pagados']].map(([val,lbl])=>(
              <button key={val} onClick={()=>setShowMsi(val)}
                style={{padding:'0.3rem 0.75rem',border:`1px solid ${showMsi===val?'#1a472a':'var(--border-color)'}`,borderRadius:'0.5rem',background:showMsi===val?'#1a472a':'var(--bg-tertiary)',color:showMsi===val?'white':'var(--text-secondary)',cursor:'pointer',fontSize:'0.78rem',fontWeight:600}}>
                {lbl}
              </button>
            ))}
          </div>
          {msiShow.map(plan=>{
            const done=plan.pending===0;
            const left=plan.totalMonths-plan.currentMonth;
            const pct=(plan.currentMonth/plan.totalMonths)*100;
            return (
              <div key={plan.id} style={{border:`1px solid ${done?'rgba(16,185,129,0.3)':'var(--border-color)'}`,borderRadius:'0.75rem',padding:'0.875rem',marginBottom:'0.5rem',background:done?'rgba(16,185,129,0.04)':'var(--bg-secondary)'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:'0.5rem'}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:'0.85rem',color:'var(--text-primary)'}}>{plan.desc}</div>
                    <div style={{fontSize:'0.7rem',color:'var(--text-secondary)',marginTop:'0.1rem'}}>{plan.person} · ${fmt(plan.origAmt)} · {fmtDate(plan.origDate)}</div>
                  </div>
                  {done
                    ? <span style={{background:'rgba(16,185,129,0.15)',color:'#10b981',borderRadius:'0.35rem',padding:'0.15rem 0.5rem',fontSize:'0.7rem',fontWeight:700,flexShrink:0}}>✓ Pagado</span>
                    : <span style={{background:'rgba(99,102,241,0.12)',color:'#6366f1',borderRadius:'0.35rem',padding:'0.15rem 0.5rem',fontSize:'0.7rem',fontWeight:700,flexShrink:0,whiteSpace:'nowrap'}}>{left} meses</span>}
                </div>
                <ProgressBar current={plan.currentMonth} total={plan.totalMonths} color={done?'#10b981':'#6366f1'}/>
                <div style={{display:'flex',justifyContent:'space-between',marginTop:'0.3rem'}}>
                  <span style={{fontSize:'0.65rem',color:'var(--text-light)'}}>Mensualidad {plan.currentMonth}/{plan.totalMonths}</span>
                  <span style={{fontSize:'0.65rem',color:'var(--text-light)'}}>{pct.toFixed(0)}% pagado</span>
                </div>
                <div style={{display:'flex',gap:'0.5rem',marginTop:'0.4rem'}}>
                  {[['Por mes',fmt(plan.monthly),'var(--text-primary)'],['Pendiente',fmt(plan.pending),done?'#10b981':'#ef4444'],['Interés','0%','#10b981']].map(([l,v,c])=>(
                    <div key={l} style={{flex:1,textAlign:'center'}}>
                      <div style={{fontSize:'0.62rem',color:'var(--text-light)'}}>{l}</div>
                      <div style={{fontSize:'0.8rem',fontWeight:700,color:c}}>{l==='Interés'?v:`$${v}`}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* ── DIFERIDOS ── */}
      {tab==='diferidos' && (
        <>
          <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:'0.5rem',marginBottom:'0.75rem'}}>
            {[['Pago mensual total',`$${fmt(totalDifMonthly)}`,'#ef4444'],['Saldo pendiente',`$${fmt(totalDifPending)}`,'#f97316']].map(([l,v,c])=>(
              <div key={l} style={{background:'rgba(239,68,68,0.06)',border:'1px solid rgba(239,68,68,0.15)',borderRadius:'0.625rem',padding:'0.5rem 0.625rem',textAlign:'center'}}>
                <div style={{fontSize:'0.65rem',color:'var(--text-secondary)'}}>{l}</div>
                <div style={{fontSize:'0.95rem',fontWeight:700,color:c}}>{v}</div>
              </div>
            ))}
          </div>
          <div style={{fontSize:'0.72rem',color:'var(--text-secondary)',marginBottom:'0.625rem',padding:'0.4rem 0.625rem',background:'rgba(245,158,11,0.08)',borderRadius:'0.5rem'}}>
            ⚠️ Comisión mensual del 3% sobre el monto diferido (CAT promedio 78.4%).
          </div>
          {(selected.pagosDiferidos??[]).map(plan=>{
            const done=plan.pending===0;
            const pct=(plan.currentMonth/plan.totalMonths)*100;
            const left=plan.totalMonths-plan.currentMonth;
            const com=plan.montoDiferido*(plan.comision/100);
            const cap=plan.monthly-com-(com*0.16);
            return (
              <div key={plan.id} style={{border:`1px solid ${done?'rgba(16,185,129,0.3)':'rgba(239,68,68,0.25)'}`,borderRadius:'0.75rem',padding:'0.875rem',marginBottom:'0.5rem',background:done?'rgba(16,185,129,0.04)':'rgba(239,68,68,0.03)'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'0.3rem'}}>
                  <div>
                    <div style={{fontWeight:700,fontSize:'0.85rem',color:'var(--text-primary)'}}>Plan de Pagos Diferidos</div>
                    <div style={{fontSize:'0.7rem',color:'var(--text-secondary)'}}>Creado {fmtDate(plan.origDate)} · ${fmt(plan.montoDiferido)}</div>
                  </div>
                  {done
                    ? <span style={{background:'rgba(16,185,129,0.15)',color:'#10b981',borderRadius:'0.35rem',padding:'0.15rem 0.5rem',fontSize:'0.7rem',fontWeight:700}}>✓ Pagado</span>
                    : <span style={{background:'rgba(239,68,68,0.12)',color:'#ef4444',borderRadius:'0.35rem',padding:'0.15rem 0.5rem',fontSize:'0.7rem',fontWeight:700,whiteSpace:'nowrap'}}>{left} meses</span>}
                </div>
                <ProgressBar current={plan.currentMonth} total={plan.totalMonths} color={done?'#10b981':'#ef4444'}/>
                <div style={{display:'flex',justifyContent:'space-between',marginTop:'0.3rem',marginBottom:'0.4rem'}}>
                  <span style={{fontSize:'0.65rem',color:'var(--text-light)'}}>Mensualidad {plan.currentMonth}/{plan.totalMonths}</span>
                  <span style={{fontSize:'0.65rem',color:'var(--text-light)'}}>{pct.toFixed(0)}% pagado</span>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'0.3rem'}}>
                  {[['Mensualidad',fmt(plan.monthly),'#ef4444'],['Capital',fmt(cap),'var(--text-primary)'],['Comisión 3%',fmt(com),'#f97316'],['Pendiente',done?'—':fmt(plan.pending),done?'#10b981':'#f97316']].map(([l,v,c])=>(
                    <div key={l} style={{textAlign:'center',background:'var(--bg-tertiary)',borderRadius:'0.375rem',padding:'0.3rem'}}>
                      <div style={{fontSize:'0.58rem',color:'var(--text-light)'}}>{l}</div>
                      <div style={{fontSize:'0.72rem',fontWeight:700,color:c}}>{(l==='Pendiente'&&done)?v:`$${v}`}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
